import React, {
  forwardRef,
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import c from 'classnames';

import './Marquee.styl';

export interface Props {
  children: React.ReactNode;
  pause: boolean;
  speed: number;
  actionTimeout: number;
}

export interface MarqueeRefObject {
  handleOffsetLeft: (offset: number) => void;
  handleOffsetRight: (offset: number) => void;
}

const Marquee = forwardRef<MarqueeRefObject, Props>(
  ({ children, pause = false, speed = 1, actionTimeout = 3000 }, ref) => {
    const firstSetRef = useRef<HTMLDivElement | null>(null);
    const secondSetRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const currentOffsetRef = useRef(0);

    const handleOffsetIdRef = useRef<number | null>(null);

    // 记录自动开启轮播图间隔timeout
    const timeOutRef = useRef<NodeJS.Timeout | null>(null);

    // 记录主动偏移轮播图动画timeout
    const animateTimeOutRef = useRef<NodeJS.Timeout | null>(null);

    const [isHandling, setIsHandling] = useState(false);

    const [isAnimating, setIsAnimating] = useState(false);

    // 正在做动画或者外面的参数是暂停时, 那么就是正在停滞中
    const isStopping = useMemo(() => pause || isHandling, [pause, isHandling]);

    // 设置偏移style
    const setSetStyle = () => {
      const firstSet = firstSetRef.current;
      const secondSet = secondSetRef.current;

      if (firstSet && secondSet) {
        firstSet.style.transform = `translateX(${currentOffsetRef.current}px)`;
        secondSet.style.transform = `translateX(${currentOffsetRef.current}px)`;
      }
    };

    // 启动轮播图
    const runFunc = useCallback(
      (stopping: boolean) => {
        // 做一下兼容
        const requestAnimationFrame =
          window.requestAnimationFrame || window.webkitRequestAnimationFrame;
        const cancelAnimationFrame =
          window.cancelAnimationFrame || window.webkitCancelAnimationFrame;

        const firstSet = firstSetRef.current;

        if (!containerRef.current || !firstSet) return;

        // 去掉动画
        if (!stopping) {
          containerRef.current.className = 'mio-marquee';
        }

        // 如果暂停了 或者正在操作  则先暂停
        if (stopping) {
          if (handleOffsetIdRef.current) {
            cancelAnimationFrame(handleOffsetIdRef.current);
          }
        } else {
          const offsetSetFunc = () => {
            if (-currentOffsetRef.current > firstSet.offsetWidth) {
              currentOffsetRef.current += firstSet.offsetWidth;
            }
            currentOffsetRef.current -= speed;
            setSetStyle();
            handleOffsetIdRef.current = requestAnimationFrame(offsetSetFunc);
          };

          handleOffsetIdRef.current = requestAnimationFrame(offsetSetFunc);
        }
      },
      [speed],
    );

    useImperativeHandle(
      ref,
      () => ({
        // 外接调用的往左偏移
        handleOffsetLeft: (offsetMagnification = 0.5) => {
          const firstSet = firstSetRef.current;

          if (!containerRef.current || !firstSet) return;

          // 是否还在做偏移动画
          if (isAnimating) {
            return;
          }
          setIsAnimating(true);
          if (animateTimeOutRef.current) {
            clearTimeout(animateTimeOutRef.current);
          }
          animateTimeOutRef.current = setTimeout(() => {
            setIsAnimating(false);
          }, 500);

          // 设置一下 正在外界调用的主动偏移中
          setIsHandling(true);
          if (handleOffsetIdRef.current) {
            // 调用主动偏移时关闭自动偏移
            cancelAnimationFrame(handleOffsetIdRef.current);
          }

          // 此次要偏移的宽度
          const shouldOffset =
            containerRef.current.offsetWidth * offsetMagnification;

          // 如果往右偏移会漏出左边的黑边
          if (currentOffsetRef.current + shouldOffset > 0) {
            // 去掉动画
            containerRef.current.className = 'mio-marquee';
            // 往左边先挪一个宽度
            currentOffsetRef.current -= firstSet.offsetWidth;
            setSetStyle();
          }

          // 偏移container组件的比例宽度
          currentOffsetRef.current += shouldOffset;

          // 进行最终偏移之前强制重绘一下之前的改变
          containerRef.current.getBoundingClientRect();
          // 再加上动画
          containerRef.current.className = 'mio-marquee mio-marquee-stopping';
          setSetStyle();

          if (timeOutRef.current) {
            clearTimeout(timeOutRef.current);
          }
          timeOutRef.current = setTimeout(() => {
            setIsHandling(false);
          }, actionTimeout);
        },
        // 外接调用的往右偏移
        handleOffsetRight: (offsetMagnification = 0.5) => {
          const firstSet = firstSetRef.current;

          if (isAnimating || !containerRef.current || !firstSet) {
            return;
          }
          setIsAnimating(true);
          animateTimeOutRef.current = setTimeout(() => {
            setIsAnimating(false);
          }, 500);

          setIsHandling(true);
          if (handleOffsetIdRef.current) {
            cancelAnimationFrame(handleOffsetIdRef.current);
          }

          // 此次要偏移的宽度
          const shouldOffset =
            containerRef.current.offsetWidth * offsetMagnification;

          // 如果偏移后的偏移距离 + 容器宽度 大于 两个 子组件的宽度, 那么右侧就会漏出黑边
          if (
            -(currentOffsetRef.current - shouldOffset) +
              containerRef.current.offsetWidth >
            2 * firstSet.offsetWidth
          ) {
            // 去掉动画
            containerRef.current.className = 'mio-marquee';
            // 往右边挪一个宽度
            currentOffsetRef.current += firstSet.offsetWidth;
            setSetStyle();
          }

          // 偏移container组件的比例宽度
          currentOffsetRef.current -= shouldOffset;

          // 进行最终偏移之前强制重绘一下之前的改变
          containerRef.current.getBoundingClientRect();
          containerRef.current.className = 'mio-marquee mio-marquee-stopping';
          setSetStyle();

          if (timeOutRef.current) {
            clearTimeout(timeOutRef.current);
          }
          timeOutRef.current = setTimeout(() => {
            setIsHandling(false);
          }, actionTimeout);
        },
      }),
      [isAnimating, actionTimeout],
    );

    useEffect(() => {
      runFunc(isStopping);

      return () => {
        if (handleOffsetIdRef.current) {
          cancelAnimationFrame(handleOffsetIdRef.current);
        }
      };
    }, [isStopping, runFunc]);

    useEffect(
      () => () => {
        if (timeOutRef.current) {
          clearTimeout(timeOutRef.current);
        }
        if (animateTimeOutRef.current) {
          clearTimeout(animateTimeOutRef.current);
        }
      },
      [],
    );

    return (
      <div
        ref={containerRef}
        className={c('mio-marquee', { 'mio-marquee-stopping': isAnimating })}
      >
        <div
          ref={firstSetRef}
          className="mio-marquee__first-set mio-marquee__set"
        >
          {children}
        </div>
        <div
          ref={secondSetRef}
          className="mio-marquee__second-set mio-marquee__set"
        >
          {children}
        </div>
      </div>
    );
  },
);

export default Marquee;
